import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Box, Stack } from '@mui/material';
import { DataFieldCDS, RadioGroupCDS, TextFieldCDS } from '@sharedCR/coreDesignSystem/form';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FieldInputType } from 'constants/fields';
import { baseStatusOptions } from 'constants/options';
import { isFieldValueChanged } from 'utils/loanFees';
import { getDefaultDisbursementDate, getInterestCreditFundingDate } from 'utils/loanPrepaidsDates';

import {
  selectClosingDatesApplicationData,
  selectFormHiddenFieldsMap,
  selectFormLockedFieldsMap,
  selectFormLockedPage,
  selectLoanApplicationSubjectPropertyOccupancy,
  selectLoanFeesSummary,
  selectLoanPrepaidsInterestCredit,
  selectPrepaidDataLoanFeesView,
} from 'store/loanApplications/selectors';
import { selectFormHiddenPage } from 'store/loanApplications/selectors/lockedFields.selectors';

import { LoanFeesTestIds } from '../../../../../../../LoanFees.testIds';

import { interestCreditStyles } from './InterestCredit.styles';
import { getInterestCreditValues, getIsDisabledInterestCredit } from './InterestCredit.utils';

const InterestCredit = ({
  form,
  handleChange,
  validationErrors,
  isLoading,
  businessDays,
  isDisabled = false,
  closeAt,
  isHeloc = false,
  isPerDiemChanged,
}) => {
  const { fundingDate } = useSelector(selectLoanFeesSummary);
  const interestCredit = useSelector(selectLoanPrepaidsInterestCredit);
  const { prepaidInterest, prepaidInterestDays } = useSelector(selectPrepaidDataLoanFeesView);
  const loanApplication = useSelector(selectClosingDatesApplicationData);
  const subjectPropertyOccupancy = useSelector(selectLoanApplicationSubjectPropertyOccupancy);
  const lockedFieldsMap = useSelector(selectFormLockedFieldsMap(FormNames.FEES));
  const hiddenFieldsMap = useSelector(selectFormHiddenFieldsMap(FormNames.FEES));
  const isPageLocked = useSelector(selectFormLockedPage(FormNames.FEES));
  const isPageHidden = useSelector(selectFormHiddenPage(FormNames.FEES));

  const interestCreditFundingDate = useMemo(
    () =>
      getInterestCreditFundingDate({
        interestCredit: {
          ...interestCredit,
          interestCreditFundingDate: interestCredit.interestCreditFundingDate || fundingDate,
        },
        loanApplication,
        businessDays,
        closeAt,
        subjectPropertyOccupancy,
        withTimezoneShift: true,
      }),
    [loanApplication, subjectPropertyOccupancy, interestCredit, fundingDate, closeAt, businessDays],
  );

  // Interest credit is earned from the day the money moves, which a Saturday funding date pushes
  // to the next business day.
  const interestCreditDisbursementDate = useMemo(
    () => getDefaultDisbursementDate(interestCreditFundingDate, businessDays),
    [interestCreditFundingDate, businessDays],
  );

  const isDisabledInterestCredit = useMemo(
    () => getIsDisabledInterestCredit(interestCreditDisbursementDate),
    [interestCreditDisbursementDate],
  );

  const { perDiem, perDiemRaw, days, total } = useMemo(
    () =>
      getInterestCreditValues({
        prepaidInterestDays,
        prepaidInterest,
        interestCreditFundingDate: interestCreditDisbursementDate,
        hasInterestCredit: form.hasInterestCredit,
        manualDailyPrepaidInterest: isHeloc ? form.manualDailyPrepaidInterest : null,
      }),
    [
      prepaidInterestDays,
      prepaidInterest,
      interestCreditDisbursementDate,
      form.hasInterestCredit,
      isHeloc,
      form.manualDailyPrepaidInterest,
    ],
  );

  const isFormDisabled = isDisabled || isLoading || isDisabledInterestCredit;
  const isDisabledByLock = isFormDisabled || isPageLocked;
  const isPerDiemDisabled = !isHeloc || !days;

  if (hiddenFieldsMap.hasInterestCredit || isPageHidden) {
    return null;
  }

  return (
    <Stack sx={interestCreditStyles}>
      <RadioGroupCDS
        required
        row
        containerClassName="radio-group"
        data-is-field-changed={isFieldValueChanged(form, 'hasInterestCredit')}
        dataTestId={LoanFeesTestIds.INTEREST_CREDIT_RADIO}
        disabled={isDisabledByLock || lockedFieldsMap.hasInterestCredit}
        error={!!_.get(validationErrors, `set_interestCredit.hasInterestCredit`)}
        helperText={_.get(validationErrors, `set_interestCredit.hasInterestCredit`)}
        label="Interest Credit"
        labelPlacement="end"
        name="set_interestCredit.hasInterestCredit"
        options={baseStatusOptions}
        value={form.hasInterestCredit}
        onChange={handleChange}
      />
      <Stack className="inputs-container">
        <Box className="text-field-wrapper">
          <DataFieldCDS
            fullWidth
            disabled={isPerDiemDisabled}
            id="per-diem"
            isFieldChanged={isPerDiemChanged}
            label="Per Diem"
            name="set_interestCredit.manualDailyPrepaidInterest"
            type={FieldInputType.CURRENCY}
            value={isHeloc ? (form.manualDailyPrepaidInterest ?? perDiemRaw) : perDiem}
            onChange={isPerDiemDisabled ? _.noop : handleChange}
          />
        </Box>
        <Box className="text-field-wrapper">
          <TextFieldCDS
            disabled
            fullWidth
            dataTestId={LoanFeesTestIds.INTEREST_CREDIT_DAYS_INPUT}
            id="days"
            label="Days"
            name="days"
            value={days}
          />
        </Box>
        <Box className="text-field-wrapper">
          <DataFieldCDS
            disabled
            fullWidth
            id="total"
            label="Total"
            name="total"
            type={FieldInputType.CURRENCY}
            value={total}
          />
        </Box>
      </Stack>
    </Stack>
  );
};

InterestCredit.propTypes = {
  form: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  validationErrors: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  businessDays: PropTypes.object.isRequired,
  closeAt: PropTypes.string,
  isDisabled: PropTypes.bool,
  isHeloc: PropTypes.bool,
  isPerDiemChanged: PropTypes.bool,
};

export default InterestCredit;
